import React, { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import {
  addUser,
  deleteUser,
  getGoogleSheetStatus,
  getUsers,
  refreshGoogleSheetToken,
  updateUser,
} from "../services/UserServices";
import useAuthStore from "../store/useAuthStore";

const initialUserForm = { name: "", email: "", role: "user", pin: "" };

const Icon = ({ name, className = "h-5 w-5" }) => {
  const paths = {
    users: (
      <>
        <circle cx="9" cy="8" r="3" />
        <path d="M3 20a6 6 0 0 1 12 0M16 5a3 3 0 0 1 0 6M17 14a5 5 0 0 1 4 5" />
      </>
    ),
    admin: (
      <>
        <path d="M12 3 4.5 6v5.2c0 4.7 3.2 8.5 7.5 9.8 4.3-1.3 7.5-5.1 7.5-9.8V6L12 3Z" />
        <path d="m9 12 2 2 4-4" />
      </>
    ),
    plus: <path d="M12 5v14M5 12h14" />,
    search: (
      <>
        <circle cx="11" cy="11" r="7" />
        <path d="m20 20-4-4" />
      </>
    ),
    edit: (
      <>
        <path d="m14 5 5 5M4 20l4.5-1 10-10a2 2 0 0 0-5-5l-10 10L4 20Z" />
        <path d="m12.5 5.5 5 5" />
      </>
    ),
    delete: (
      <>
        <path d="M4 7h16M9 7V4h6v3M6 7l1 14h10l1-14M10 11v6M14 11v6" />
      </>
    ),
    sheet: (
      <>
        <path d="M6 3h9l4 4v14H6z" />
        <path d="M14 3v5h5M9 12h7M9 16h7M12 9v10" />
      </>
    ),
    refresh: (
      <>
        <path d="M20 6v5h-5M4 18v-5h5" />
        <path d="M18.5 10A7 7 0 0 0 6 7.5L4 11M5.5 14A7 7 0 0 0 18 16.5l2-3.5" />
      </>
    ),
    external: (
      <>
        <path d="M14 4h6v6M20 4l-9 9" />
        <path d="M18 13v6a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V7a1 1 0 0 1 1-1h6" />
      </>
    ),
    close: <path d="m6 6 12 12M18 6 6 18" />,
    check: <path d="m5 12 4 4L19 6" />,
    alert: (
      <>
        <path d="M12 3 2.5 20h19L12 3Z" />
        <path d="M12 9v4M12 17h.01" />
      </>
    ),
    mail: (
      <>
        <rect x="3" y="5" width="18" height="14" rx="2" />
        <path d="m4 7 8 6 8-6" />
      </>
    ),
    key: (
      <>
        <circle cx="8" cy="15" r="4" />
        <path d="m11 12 8-8M16 7l2 2M14 9l2 2" />
      </>
    ),
    eye: (
      <>
        <path d="M2.5 12s3.5-6 9.5-6 9.5 6 9.5 6-3.5 6-9.5 6-9.5-6-9.5-6Z" />
        <circle cx="12" cy="12" r="2.5" />
      </>
    ),
    eyeOff: (
      <>
        <path d="m3 3 18 18M10.6 6.2A9.8 9.8 0 0 1 12 6c6 0 9.5 6 9.5 6a16 16 0 0 1-2.1 2.8M6.2 6.2C3.8 7.8 2.5 12 2.5 12s3.5 6 9.5 6a9.8 9.8 0 0 0 3-.5M10.3 10.3a2.5 2.5 0 0 0 3.4 3.4" />
      </>
    ),
    chevron: <path d="m9 18 6-6-6-6" />,
  };

  return (
    <svg
      aria-hidden="true"
      className={className}
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      {paths[name]}
    </svg>
  );
};

const roleLabels = {
  admin: "Admin",
  "sub-admin": "Sub-admin",
  user: "User",
};

const roleStyles = {
  admin:
    "border-indigo-200 bg-indigo-50 text-indigo-700 dark:border-indigo-800 dark:bg-indigo-500/10 dark:text-indigo-300",
  "sub-admin":
    "border-violet-200 bg-violet-50 text-violet-700 dark:border-violet-800 dark:bg-violet-500/10 dark:text-violet-300",
  user: "border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-800 dark:bg-emerald-500/10 dark:text-emerald-300",
};

const getInitials = (name = "") => {
  const initials = name
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase())
    .join("");
  return initials || "U";
};

const RoleBadge = ({ role }) => (
  <span
    className={`inline-flex items-center rounded-full border px-2.5 py-1 text-[11px] font-bold ${
      roleStyles[role] || roleStyles.user
    }`}
  >
    {roleLabels[role] || role}
  </span>
);

const StatCard = ({ icon, label, value, accent }) => (
  <article className="rounded-2xl border border-slate-200 bg-white p-3 shadow-sm dark:border-slate-800 dark:bg-slate-900 sm:p-4">
    <div className="flex flex-col items-start gap-2 sm:flex-row sm:items-center sm:gap-3">
      <span
        className={`grid h-9 w-9 shrink-0 place-items-center rounded-xl sm:h-10 sm:w-10 ${accent}`}
      >
        <Icon name={icon} className="h-[18px] w-[18px] sm:h-5 sm:w-5" />
      </span>
      <div>
        <p className="text-xl font-bold tracking-tight text-slate-950 dark:text-white sm:text-2xl">
          {value}
        </p>
        <p className="text-[10px] font-semibold leading-4 text-slate-500 dark:text-slate-400 sm:text-xs">
          {label}
        </p>
      </div>
    </div>
  </article>
);

const Field = ({ label, icon, children }) => (
  <label className="block">
    <span className="mb-1.5 block text-xs font-bold text-slate-700 dark:text-slate-300">
      {label}
    </span>
    <span className="relative block">
      <Icon
        name={icon}
        className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400"
      />
      {children}
    </span>
  </label>
);

const inputClass =
  "min-h-12 w-full rounded-xl border border-slate-200 bg-white px-3.5 pl-10 text-sm text-slate-900 outline-none transition placeholder:text-slate-400 focus:border-indigo-500 focus:ring-4 focus:ring-indigo-100 dark:border-slate-700 dark:bg-slate-950 dark:text-white dark:focus:ring-indigo-950";

const Users = () => {
  const token = useAuthStore((state) => state.token);
  const isInternal = useAuthStore((state) => state.isInternal);
  const navigate = useNavigate();
  const location = useLocation();
  const handledQueryRef = useRef("");

  const [userList, setUserList] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(true);
  const [editingUser, setEditingUser] = useState(null);
  const [addingUser, setAddingUser] = useState(false);
  const [savingUser, setSavingUser] = useState(false);
  const [deletingUserId, setDeletingUserId] = useState("");
  const [formData, setFormData] = useState(initialUserForm);
  const [showFormPin, setShowFormPin] = useState(false);
  const [visiblePinIds, setVisiblePinIds] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [roleFilter, setRoleFilter] = useState("all");

  const [googleStatus, setGoogleStatus] = useState({
    loading: true,
    data: null,
    error: "",
  });
  const [showCreateSheetModal, setShowCreateSheetModal] = useState(false);
  const [newSheetName, setNewSheetName] = useState("");
  const [connectingSheet, setConnectingSheet] = useState(false);
  const [refreshingToken, setRefreshingToken] = useState(false);

  const connectedSheetUrl = useMemo(() => {
    const sheetId = googleStatus?.data?.sheetId;
    return sheetId ? `https://docs.google.com/spreadsheets/d/${sheetId}` : "";
  }, [googleStatus?.data?.sheetId]);

  const stats = useMemo(
    () => ({
      total: userList.length,
      admins: userList.filter((user) => user.role === "admin").length,
      members: userList.filter((user) => user.role !== "admin").length,
    }),
    [userList]
  );

  const filteredUsers = useMemo(() => {
    const query = searchTerm.trim().toLowerCase();
    return userList.filter((user) => {
      const matchesRole = roleFilter === "all" || user.role === roleFilter;
      const matchesQuery =
        !query ||
        user.name?.toLowerCase().includes(query) ||
        user.email?.toLowerCase().includes(query) ||
        roleLabels[user.role]?.toLowerCase().includes(query);
      return matchesRole && matchesQuery;
    });
  }, [roleFilter, searchTerm, userList]);

  const getUsersList = async () => {
    setLoadingUsers(true);
    try {
      const response = await getUsers();
      setUserList(response?.data?.data || []);
    } catch (error) {
      toast.error(error?.response?.data?.message || "Failed to fetch users");
    } finally {
      setLoadingUsers(false);
    }
  };

  const loadGoogleStatus = async ({ silent = false } = {}) => {
    if (!silent) {
      setGoogleStatus((previous) => ({
        ...previous,
        loading: true,
        error: "",
      }));
    }

    try {
      const response = await getGoogleSheetStatus();
      setGoogleStatus({
        loading: false,
        data: response?.data || null,
        error: "",
      });
      return true;
    } catch (error) {
      const message =
        error?.response?.data?.message || "Failed to fetch Google Sheet status";
      setGoogleStatus({ loading: false, data: null, error: message });
      if (!silent) toast.error(message);
      return false;
    }
  };

  useEffect(() => {
    if (!token) {
      navigate("/");
      return;
    }

    getUsersList();
    loadGoogleStatus({ silent: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  useEffect(() => {
    if (!location.search || handledQueryRef.current === location.search) return;
    handledQueryRef.current = location.search;

    const params = new URLSearchParams(location.search);
    const googleFlag = params.get("google");
    const reason = params.get("reason");

    if (googleFlag === "connected") {
      toast.success("Google Sheet connected successfully");
      loadGoogleStatus({ silent: true });
      window.history.replaceState({}, "", "/users");
    } else if (googleFlag === "error") {
      toast.error(reason || "Google connection failed");
      window.history.replaceState({}, "", "/users");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  const closeUserForm = () => {
    if (savingUser) return;
    setAddingUser(false);
    setEditingUser(null);
    setFormData(initialUserForm);
    setShowFormPin(false);
  };

  const openAddUser = () => {
    setEditingUser(null);
    setAddingUser(true);
    setFormData(initialUserForm);
    setShowFormPin(false);
  };

  const openEditUser = (user) => {
    setAddingUser(false);
    setEditingUser(user);
    setFormData({
      name: user.name || "",
      email: user.email || "",
      role: user.role || "user",
      pin: user.pin || "",
    });
    setShowFormPin(false);
  };

  const validateUserForm = () => {
    const normalized = {
      name: formData.name.trim(),
      email: formData.email.trim().toLowerCase(),
      role: formData.role,
      pin: formData.pin.trim(),
    };

    if (!normalized.name || !normalized.email || !normalized.role) {
      toast.error("Name, email, and role are required");
      return null;
    }
    if (!/^\S+@\S+\.\S+$/.test(normalized.email)) {
      toast.error("Enter a valid email address");
      return null;
    }
    if (!/^\d{4}$/.test(normalized.pin)) {
      toast.error("PIN must contain exactly 4 digits");
      return null;
    }
    return normalized;
  };

  const handleUserSubmit = async (event) => {
    event.preventDefault();
    const payload = validateUserForm();
    if (!payload) return;

    setSavingUser(true);
    try {
      if (editingUser) {
        await updateUser({ id: editingUser.id, ...payload });
        setUserList((previous) =>
          previous.map((user) =>
            user.id === editingUser.id ? { ...user, ...payload } : user
          )
        );
        toast.success("User updated successfully");
      } else {
        await addUser(payload);
        await getUsersList();
        toast.success("User added successfully");
      }
      setAddingUser(false);
      setEditingUser(null);
      setFormData(initialUserForm);
      setShowFormPin(false);
    } catch (error) {
      toast.error(
        error?.response?.data?.message ||
          (editingUser ? "Failed to update user" : "Failed to add user")
      );
    } finally {
      setSavingUser(false);
    }
  };

  const handleDelete = async (user) => {
    if (
      !window.confirm(
        `Delete ${user.name}? They will immediately lose access to the workspace.`
      )
    ) {
      return;
    }

    setDeletingUserId(user.id);
    try {
      await deleteUser(user.id);
      setUserList((previous) =>
        previous.filter((item) => item.id !== user.id)
      );
      toast.success("User deleted successfully");
    } catch (error) {
      toast.error(error?.response?.data?.message || "Failed to delete user");
    } finally {
      setDeletingUserId("");
    }
  };

  const getConnectBaseUrl = () => {
    const configuredBase = isInternal
      ? process.env.REACT_APP_BACKEND_URL
      : process.env.REACT_APP_CLIENT_URL;
    const baseUrl = configuredBase || "http://localhost:4069";
    return `${baseUrl.replace(/\/$/, "")}/api/auth/auth`;
  };

  const redirectToGoogleConnect = ({ createNew, sheetName = "" }) => {
    if (!token) {
      toast.error("Session expired. Please login again.");
      return;
    }

    const trimmedSheetName = sheetName.trim();
    if (createNew && !trimmedSheetName) {
      toast.error("Sheet name is required");
      return;
    }

    setConnectingSheet(true);
    const queryParams = new URLSearchParams({
      token,
      createNew: createNew ? "true" : "false",
      redirectUri: `${window.location.origin}/users`,
    });
    if (createNew) queryParams.set("sheetName", trimmedSheetName);
    window.location.href = `${getConnectBaseUrl()}?${queryParams.toString()}`;
  };

  const handleRefreshGoogleStatus = async () => {
    const refreshed = await loadGoogleStatus();
    if (refreshed) toast.success("Google Sheet status refreshed");
  };

  const handleRefreshToken = async () => {
    setRefreshingToken(true);
    try {
      await refreshGoogleSheetToken();
      toast.success("Google token refreshed");
      await loadGoogleStatus({ silent: true });
    } catch (error) {
      toast.error(
        error?.response?.data?.message ||
          "Token refresh failed. Please reconnect Google."
      );
      await loadGoogleStatus({ silent: true });
    } finally {
      setRefreshingToken(false);
    }
  };

  const connectionValid =
    googleStatus.data?.connected && googleStatus.data?.tokenStatus === "valid";
  const userFormOpen = addingUser || Boolean(editingUser);

  const togglePinVisibility = (userId) => {
    setVisiblePinIds((previous) =>
      previous.includes(userId)
        ? previous.filter((id) => id !== userId)
        : [...previous, userId]
    );
  };

  const renderUserPin = (user, compact = false) => {
    const isVisible = visiblePinIds.includes(user.id);

    return (
      <div
        className={`flex items-center justify-between gap-2 rounded-xl bg-slate-50 dark:bg-slate-950 ${
          compact ? "mt-3 px-3 py-2" : "max-w-[9rem] px-2.5 py-1.5"
        }`}
      >
        <div className="min-w-0">
          {compact && (
            <span className="block text-[9px] font-bold uppercase tracking-[0.12em] text-slate-400">
              Sign-in PIN
            </span>
          )}
          <code
            className={`block font-bold text-slate-700 dark:text-slate-200 ${
              isVisible ? "tracking-[0.15em]" : "tracking-[0.25em]"
            }`}
          >
            {isVisible ? user.pin || "Not set" : "••••"}
          </code>
        </div>
        <button
          type="button"
          onClick={() => togglePinVisibility(user.id)}
          aria-label={`${isVisible ? "Hide" : "Show"} PIN for ${user.name}`}
          title={`${isVisible ? "Hide" : "Show"} PIN`}
          className="grid h-8 w-8 shrink-0 place-items-center rounded-lg text-slate-400 transition hover:bg-white hover:text-indigo-600 dark:hover:bg-slate-800 dark:hover:text-indigo-300"
        >
          <Icon name={isVisible ? "eyeOff" : "eye"} className="h-4 w-4" />
        </button>
      </div>
    );
  };

  const renderActions = (user, compact = false) => (
    <div className={`flex items-center ${compact ? "gap-2" : "justify-end gap-1"}`}>
      <button
        type="button"
        onClick={() => openEditUser(user)}
        aria-label={`Edit ${user.name}`}
        className={`inline-flex items-center justify-center gap-2 rounded-xl border border-slate-200 font-bold text-slate-600 transition hover:border-indigo-200 hover:bg-indigo-50 hover:text-indigo-700 dark:border-slate-700 dark:text-slate-300 dark:hover:border-indigo-800 dark:hover:bg-indigo-950/40 dark:hover:text-indigo-300 ${
          compact ? "min-h-10 flex-1 px-3 text-xs" : "h-9 w-9"
        }`}
      >
        <Icon name="edit" className="h-4 w-4" />
        {compact && "Edit"}
      </button>
      <button
        type="button"
        onClick={() => handleDelete(user)}
        disabled={deletingUserId === user.id}
        aria-label={`Delete ${user.name}`}
        className={`inline-flex items-center justify-center gap-2 rounded-xl border border-rose-200 font-bold text-rose-600 transition hover:bg-rose-50 disabled:cursor-wait disabled:opacity-50 dark:border-rose-900 dark:text-rose-300 dark:hover:bg-rose-950/40 ${
          compact ? "min-h-10 flex-1 px-3 text-xs" : "h-9 w-9"
        }`}
      >
        <Icon name="delete" className="h-4 w-4" />
        {compact &&
          (deletingUserId === user.id ? "Deleting…" : "Delete")}
      </button>
    </div>
  );

  return (
    <main className="w-full px-4 py-5 sm:px-6 sm:py-8 lg:px-8">
      <div className="mx-auto max-w-7xl">
        <header className="relative overflow-hidden rounded-3xl border border-indigo-100 bg-gradient-to-br from-white via-indigo-50/70 to-violet-100/70 px-5 py-6 shadow-sm dark:border-slate-800 dark:from-slate-900 dark:via-slate-900 dark:to-indigo-950/70 sm:px-8 sm:py-8">
          <div className="pointer-events-none absolute -right-16 -top-20 h-52 w-52 rounded-full bg-indigo-300/20 blur-3xl" />
          <div className="relative flex flex-col gap-5 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <span className="inline-flex items-center gap-2 rounded-full bg-indigo-600 px-3 py-1.5 text-[11px] font-bold uppercase tracking-[0.15em] text-white">
                <Icon name="admin" className="h-4 w-4" /> Admin console
              </span>
              <h1 className="mt-4 text-2xl font-bold tracking-tight text-slate-950 dark:text-white sm:text-3xl">
                Team access
              </h1>
              <p className="mt-2 max-w-2xl text-sm leading-6 text-slate-600 dark:text-slate-400 sm:text-base">
                Add teammates, control their workspace permissions, and manage
                the Google Sheet used for saved cards.
              </p>
            </div>
            <button
              type="button"
              onClick={openAddUser}
              className="inline-flex min-h-12 items-center justify-center gap-2 rounded-xl bg-slate-950 px-5 text-sm font-bold text-white shadow-lg transition hover:-translate-y-0.5 hover:bg-slate-800 dark:bg-white dark:text-slate-950 dark:hover:bg-slate-200"
            >
              <Icon name="plus" className="h-5 w-5" /> Add user
            </button>
          </div>
        </header>

        <section
          aria-label="User overview"
          className="mt-5 grid grid-cols-3 gap-2 sm:gap-4"
        >
          <StatCard
            icon="users"
            label="Total users"
            value={loadingUsers ? "—" : stats.total}
            accent="bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300"
          />
          <StatCard
            icon="admin"
            label="Administrators"
            value={loadingUsers ? "—" : stats.admins}
            accent="bg-indigo-50 text-indigo-700 dark:bg-indigo-500/10 dark:text-indigo-300"
          />
          <StatCard
            icon="users"
            label="Team members"
            value={loadingUsers ? "—" : stats.members}
            accent="bg-emerald-50 text-emerald-700 dark:bg-emerald-500/10 dark:text-emerald-300"
          />
        </section>

        <section className="mt-5 overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-900 sm:mt-6">
          <div className="flex flex-col gap-5 p-4 sm:p-6 lg:flex-row lg:items-start lg:justify-between">
            <div className="flex min-w-0 items-start gap-3 sm:gap-4">
              <span
                className={`grid h-12 w-12 shrink-0 place-items-center rounded-2xl ${
                  connectionValid
                    ? "bg-emerald-50 text-emerald-700 dark:bg-emerald-500/10 dark:text-emerald-300"
                    : "bg-amber-50 text-amber-700 dark:bg-amber-500/10 dark:text-amber-300"
                }`}
              >
                <Icon name="sheet" className="h-6 w-6" />
              </span>
              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                  <h2 className="text-base font-bold text-slate-950 dark:text-white sm:text-lg">
                    Google Sheet connection
                  </h2>
                  {!googleStatus.loading && !googleStatus.error && (
                    <span
                      className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[10px] font-bold uppercase tracking-wide ${
                        connectionValid
                          ? "bg-emerald-50 text-emerald-700 dark:bg-emerald-500/10 dark:text-emerald-300"
                          : "bg-amber-50 text-amber-700 dark:bg-amber-500/10 dark:text-amber-300"
                      }`}
                    >
                      <span
                        className={`h-1.5 w-1.5 rounded-full ${
                          connectionValid ? "bg-emerald-500" : "bg-amber-500"
                        }`}
                      />
                      {connectionValid ? "Connected" : "Needs attention"}
                    </span>
                  )}
                </div>

                {googleStatus.loading ? (
                  <div className="mt-3 space-y-2" aria-label="Checking Google connection">
                    <div className="h-3 w-52 animate-pulse rounded bg-slate-200 dark:bg-slate-800" />
                    <div className="h-3 w-36 animate-pulse rounded bg-slate-100 dark:bg-slate-800/70" />
                  </div>
                ) : googleStatus.error ? (
                  <div className="mt-3 flex max-w-xl items-start gap-2 rounded-xl bg-rose-50 p-3 text-xs leading-5 text-rose-700 dark:bg-rose-950/40 dark:text-rose-300">
                    <Icon name="alert" className="mt-0.5 h-4 w-4 shrink-0" />
                    {googleStatus.error}
                  </div>
                ) : googleStatus.data?.connected ? (
                  <div className="mt-3 grid gap-2 text-xs text-slate-600 dark:text-slate-400 sm:grid-cols-2 sm:text-sm">
                    <p className="min-w-0">
                      <span className="block text-[10px] font-bold uppercase tracking-wide text-slate-400">
                        Account
                      </span>
                      <span className="mt-0.5 block truncate font-semibold text-slate-800 dark:text-slate-200">
                        {googleStatus.data.ownerEmail || "Not available"}
                      </span>
                    </p>
                    <p className="min-w-0">
                      <span className="block text-[10px] font-bold uppercase tracking-wide text-slate-400">
                        Spreadsheet
                      </span>
                      <span className="mt-0.5 block truncate font-semibold text-slate-800 dark:text-slate-200">
                        {googleStatus.data.sheetName || "Not available"}
                      </span>
                    </p>
                    {googleStatus.data.sheetError && (
                      <p className="flex items-start gap-2 rounded-lg bg-amber-50 p-2 text-xs text-amber-700 dark:bg-amber-950/40 dark:text-amber-300 sm:col-span-2">
                        <Icon name="alert" className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                        {googleStatus.data.sheetError}
                      </p>
                    )}
                  </div>
                ) : (
                  <p className="mt-2 max-w-xl text-sm leading-6 text-slate-500 dark:text-slate-400">
                    Connect a spreadsheet to automatically add saved card data
                    to Google Sheets.
                  </p>
                )}
              </div>
            </div>

            <div className="grid shrink-0 grid-cols-2 gap-2 sm:flex sm:flex-wrap lg:max-w-md lg:justify-end">
              {connectedSheetUrl && (
                <a
                  href={connectedSheetUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="col-span-2 inline-flex min-h-10 items-center justify-center gap-2 rounded-xl border border-emerald-200 bg-emerald-50 px-3 text-xs font-bold text-emerald-700 transition hover:bg-emerald-100 dark:border-emerald-900 dark:bg-emerald-950/40 dark:text-emerald-300 dark:hover:bg-emerald-950/70 sm:col-span-1"
                >
                  <Icon name="external" className="h-4 w-4" /> Open sheet
                </a>
              )}
              <button
                type="button"
                onClick={() => redirectToGoogleConnect({ createNew: false })}
                disabled={connectingSheet}
                className="inline-flex min-h-10 items-center justify-center gap-2 rounded-xl bg-indigo-600 px-3 text-xs font-bold text-white transition hover:bg-indigo-700 disabled:opacity-50"
              >
                <Icon name="sheet" className="h-4 w-4" />
                {googleStatus.data?.connected ? "Reconnect" : "Connect"}
              </button>
              <button
                type="button"
                onClick={() => {
                  setNewSheetName("");
                  setShowCreateSheetModal(true);
                }}
                disabled={connectingSheet}
                className="inline-flex min-h-10 items-center justify-center gap-2 rounded-xl border border-slate-200 px-3 text-xs font-bold text-slate-600 transition hover:border-indigo-200 hover:bg-indigo-50 hover:text-indigo-700 disabled:opacity-50 dark:border-slate-700 dark:text-slate-300 dark:hover:border-indigo-800 dark:hover:bg-indigo-950/40 dark:hover:text-indigo-300"
              >
                <Icon name="plus" className="h-4 w-4" /> New sheet
              </button>
              <button
                type="button"
                onClick={handleRefreshGoogleStatus}
                disabled={googleStatus.loading}
                className="inline-flex min-h-10 items-center justify-center gap-2 rounded-xl border border-slate-200 px-3 text-xs font-bold text-slate-600 transition hover:bg-slate-50 disabled:opacity-50 dark:border-slate-700 dark:text-slate-300 dark:hover:bg-slate-800"
              >
                <Icon
                  name="refresh"
                  className={`h-4 w-4 ${googleStatus.loading ? "animate-spin" : ""}`}
                />
                Refresh
              </button>
              {googleStatus.data?.needsReconnect && (
                <button
                  type="button"
                  onClick={handleRefreshToken}
                  disabled={refreshingToken}
                  className="col-span-2 inline-flex min-h-10 items-center justify-center gap-2 rounded-xl bg-amber-500 px-3 text-xs font-bold text-white transition hover:bg-amber-600 disabled:opacity-50 sm:col-span-1"
                >
                  <Icon
                    name="refresh"
                    className={`h-4 w-4 ${refreshingToken ? "animate-spin" : ""}`}
                  />
                  {refreshingToken ? "Refreshing…" : "Refresh token"}
                </button>
              )}
            </div>
          </div>
        </section>

        <section className="mt-5 overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-900 sm:mt-6">
          <header className="border-b border-slate-100 p-4 dark:border-slate-800 sm:p-6">
            <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
              <div>
                <p className="text-[10px] font-bold uppercase tracking-[0.16em] text-indigo-600 dark:text-indigo-300">
                  User directory
                </p>
                <h2 className="mt-1 text-xl font-bold text-slate-950 dark:text-white">
                  Workspace members
                </h2>
                <p className="mt-1 text-xs leading-5 text-slate-500 dark:text-slate-400">
                  {loadingUsers
                    ? "Loading team members…"
                    : `${filteredUsers.length} of ${userList.length} users shown`}
                </p>
              </div>

              <div className="grid gap-2 sm:grid-cols-[minmax(0,18rem)_10rem]">
                <label className="relative block">
                  <span className="sr-only">Search users</span>
                  <Icon
                    name="search"
                    className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400"
                  />
                  <input
                    type="search"
                    value={searchTerm}
                    onChange={(event) => setSearchTerm(event.target.value)}
                    placeholder="Search name or email"
                    className="min-h-11 w-full rounded-xl border border-slate-200 bg-slate-50 pl-10 pr-3 text-sm text-slate-900 outline-none transition placeholder:text-slate-400 focus:border-indigo-500 focus:bg-white focus:ring-4 focus:ring-indigo-100 dark:border-slate-700 dark:bg-slate-950 dark:text-white dark:focus:ring-indigo-950"
                  />
                </label>
                <label className="relative block">
                  <span className="sr-only">Filter by role</span>
                  <select
                    value={roleFilter}
                    onChange={(event) => setRoleFilter(event.target.value)}
                    className="min-h-11 w-full appearance-none rounded-xl border border-slate-200 bg-slate-50 px-3 pr-9 text-sm font-semibold text-slate-700 outline-none transition focus:border-indigo-500 focus:bg-white focus:ring-4 focus:ring-indigo-100 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-200 dark:focus:ring-indigo-950"
                  >
                    <option value="all">All roles</option>
                    <option value="admin">Admins</option>
                    <option value="sub-admin">Sub-admins</option>
                    <option value="user">Users</option>
                  </select>
                  <Icon
                    name="chevron"
                    className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 rotate-90 text-slate-400"
                  />
                </label>
              </div>
            </div>
          </header>

          {loadingUsers ? (
            <div className="grid gap-3 p-4 sm:grid-cols-2 lg:grid-cols-4">
              {[0, 1, 2, 3].map((item) => (
                <div
                  key={item}
                  className="h-36 animate-pulse rounded-2xl bg-slate-100 dark:bg-slate-800"
                />
              ))}
            </div>
          ) : filteredUsers.length === 0 ? (
            <div className="px-5 py-14 text-center">
              <span className="mx-auto grid h-14 w-14 place-items-center rounded-2xl bg-slate-100 text-slate-400 dark:bg-slate-800">
                <Icon name="search" className="h-6 w-6" />
              </span>
              <h3 className="mt-4 text-base font-bold text-slate-900 dark:text-white">
                No users found
              </h3>
              <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
                Try a different search term or role filter.
              </p>
            </div>
          ) : (
            <>
              <div className="grid gap-3 p-4 sm:grid-cols-2 lg:hidden">
                {filteredUsers.map((user) => (
                  <article
                    key={user.id}
                    className="rounded-2xl border border-slate-200 p-4 dark:border-slate-700"
                  >
                    <div className="flex min-w-0 items-start gap-3">
                      <span className="grid h-11 w-11 shrink-0 place-items-center rounded-2xl bg-gradient-to-br from-indigo-500 to-violet-600 text-sm font-bold text-white shadow-sm">
                        {getInitials(user.name)}
                      </span>
                      <div className="min-w-0 flex-1">
                        <h3 className="truncate text-sm font-bold text-slate-950 dark:text-white">
                          {user.name}
                        </h3>
                        <p className="mt-0.5 truncate text-xs text-slate-500 dark:text-slate-400">
                          {user.email}
                        </p>
                        <div className="mt-2">
                          <RoleBadge role={user.role} />
                        </div>
                      </div>
                    </div>
                    {renderUserPin(user, true)}
                    <div className="mt-4 border-t border-slate-100 pt-3 dark:border-slate-800">
                      {renderActions(user, true)}
                    </div>
                  </article>
                ))}
              </div>

              <div className="hidden overflow-x-auto lg:block">
                <table className="w-full text-left">
                  <thead>
                    <tr className="border-b border-slate-100 bg-slate-50/80 text-[10px] font-bold uppercase tracking-[0.13em] text-slate-400 dark:border-slate-800 dark:bg-slate-950/50">
                      <th className="px-6 py-3">Member</th>
                      <th className="px-4 py-3">Email</th>
                      <th className="px-4 py-3">Role</th>
                      <th className="px-4 py-3">PIN</th>
                      <th className="px-6 py-3 text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                    {filteredUsers.map((user) => (
                      <tr
                        key={user.id}
                        className="transition hover:bg-slate-50/80 dark:hover:bg-slate-800/40"
                      >
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-3">
                            <span className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-gradient-to-br from-indigo-500 to-violet-600 text-xs font-bold text-white">
                              {getInitials(user.name)}
                            </span>
                            <span className="font-bold text-slate-900 dark:text-white">
                              {user.name}
                            </span>
                          </div>
                        </td>
                        <td className="px-4 py-4 text-sm text-slate-600 dark:text-slate-400">
                          {user.email}
                        </td>
                        <td className="px-4 py-4">
                          <RoleBadge role={user.role} />
                        </td>
                        <td className="px-4 py-4">
                          {renderUserPin(user)}
                        </td>
                        <td className="px-6 py-4">{renderActions(user)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </section>
      </div>

      {userFormOpen && (
        <div
          className="fixed inset-0 z-50 flex items-end bg-slate-950/65 p-0 backdrop-blur-sm sm:items-center sm:justify-center sm:p-4"
          role="presentation"
          onMouseDown={(event) => {
            if (event.currentTarget === event.target) closeUserForm();
          }}
        >
          <section
            role="dialog"
            aria-modal="true"
            aria-labelledby="user-form-title"
            className="mobile-safe-bottom max-h-[92vh] w-full overflow-y-auto rounded-t-3xl border border-slate-200 bg-white p-5 shadow-2xl dark:border-slate-700 dark:bg-slate-900 sm:max-w-lg sm:rounded-3xl sm:p-6"
          >
            <header className="flex items-start justify-between gap-4">
              <div>
                <p className="text-[10px] font-bold uppercase tracking-[0.16em] text-indigo-600 dark:text-indigo-300">
                  {editingUser ? "Update access" : "New teammate"}
                </p>
                <h2
                  id="user-form-title"
                  className="mt-1 text-xl font-bold text-slate-950 dark:text-white"
                >
                  {editingUser ? `Edit ${editingUser.name}` : "Add a user"}
                </h2>
                <p className="mt-1 text-xs leading-5 text-slate-500 dark:text-slate-400">
                  Set their sign-in details and workspace permissions.
                </p>
              </div>
              <button
                type="button"
                onClick={closeUserForm}
                disabled={savingUser}
                aria-label="Close user form"
                className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-slate-100 text-slate-500 transition hover:bg-slate-200 disabled:opacity-50 dark:bg-slate-800 dark:text-slate-300 dark:hover:bg-slate-700"
              >
                <Icon name="close" className="h-5 w-5" />
              </button>
            </header>

            <form onSubmit={handleUserSubmit} className="mt-6 space-y-4">
              <Field label="Full name" icon="users">
                <input
                  autoFocus
                  type="text"
                  autoComplete="name"
                  value={formData.name}
                  onChange={(event) =>
                    setFormData((previous) => ({
                      ...previous,
                      name: event.target.value,
                    }))
                  }
                  placeholder="e.g. Aditi Sharma"
                  className={inputClass}
                />
              </Field>

              <Field label="Email address" icon="mail">
                <input
                  type="email"
                  autoComplete="email"
                  value={formData.email}
                  onChange={(event) =>
                    setFormData((previous) => ({
                      ...previous,
                      email: event.target.value,
                    }))
                  }
                  placeholder="name@company.com"
                  className={inputClass}
                />
              </Field>

              <div className="grid gap-4 sm:grid-cols-2">
                <Field label="Workspace role" icon="admin">
                  <select
                    value={formData.role}
                    onChange={(event) =>
                      setFormData((previous) => ({
                        ...previous,
                        role: event.target.value,
                      }))
                    }
                    className={`${inputClass} appearance-none pr-9`}
                  >
                    <option value="admin">Admin</option>
                    <option value="sub-admin">Sub-admin</option>
                    <option value="user">User</option>
                  </select>
                  <Icon
                    name="chevron"
                    className="pointer-events-none absolute right-3.5 top-1/2 h-4 w-4 -translate-y-1/2 rotate-90 text-slate-400"
                  />
                </Field>

                <Field label="4-digit PIN" icon="key">
                  <input
                    type={showFormPin ? "text" : "password"}
                    inputMode="numeric"
                    autoComplete="new-password"
                    maxLength={4}
                    value={formData.pin}
                    onChange={(event) =>
                      setFormData((previous) => ({
                        ...previous,
                        pin: event.target.value.replace(/\D/g, "").slice(0, 4),
                      }))
                    }
                    placeholder="••••"
                    className={`${inputClass} pr-12 tracking-[0.35em]`}
                  />
                  <button
                    type="button"
                    onClick={() => setShowFormPin((visible) => !visible)}
                    aria-label={showFormPin ? "Hide PIN" : "Show PIN"}
                    title={showFormPin ? "Hide PIN" : "Show PIN"}
                    className="absolute right-2 top-1/2 grid h-9 w-9 -translate-y-1/2 place-items-center rounded-lg text-slate-400 transition hover:bg-slate-100 hover:text-indigo-600 dark:hover:bg-slate-800 dark:hover:text-indigo-300"
                  >
                    <Icon
                      name={showFormPin ? "eyeOff" : "eye"}
                      className="h-4 w-4"
                    />
                  </button>
                </Field>
              </div>

              <div className="rounded-xl bg-slate-50 p-3 text-xs leading-5 text-slate-500 dark:bg-slate-950 dark:text-slate-400">
                <strong className="text-slate-700 dark:text-slate-200">
                  Role access:
                </strong>{" "}
                Admins manage the company workspace, sub-admins manage their own
                campaigns, and users can scan and save cards.
              </div>

              <div className="grid grid-cols-2 gap-2 pt-2">
                <button
                  type="button"
                  onClick={closeUserForm}
                  disabled={savingUser}
                  className="min-h-12 rounded-xl border border-slate-200 text-sm font-bold text-slate-600 transition hover:bg-slate-50 disabled:opacity-50 dark:border-slate-700 dark:text-slate-300 dark:hover:bg-slate-800"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={savingUser}
                  className="inline-flex min-h-12 items-center justify-center gap-2 rounded-xl bg-indigo-600 px-4 text-sm font-bold text-white transition hover:bg-indigo-700 disabled:cursor-wait disabled:opacity-60"
                >
                  <Icon name="check" className="h-4 w-4" />
                  {savingUser
                    ? "Saving…"
                    : editingUser
                      ? "Save changes"
                      : "Add user"}
                </button>
              </div>
            </form>
          </section>
        </div>
      )}

      {showCreateSheetModal && (
        <div
          className="fixed inset-0 z-50 flex items-end bg-slate-950/65 p-0 backdrop-blur-sm sm:items-center sm:justify-center sm:p-4"
          role="presentation"
          onMouseDown={(event) => {
            if (event.currentTarget === event.target && !connectingSheet) {
              setShowCreateSheetModal(false);
              setNewSheetName("");
            }
          }}
        >
          <section
            role="dialog"
            aria-modal="true"
            aria-labelledby="sheet-form-title"
            className="mobile-safe-bottom w-full rounded-t-3xl border border-slate-200 bg-white p-5 shadow-2xl dark:border-slate-700 dark:bg-slate-900 sm:max-w-md sm:rounded-3xl sm:p-6"
          >
            <header className="flex items-start justify-between gap-4">
              <div>
                <span className="grid h-11 w-11 place-items-center rounded-2xl bg-emerald-50 text-emerald-700 dark:bg-emerald-500/10 dark:text-emerald-300">
                  <Icon name="sheet" className="h-5 w-5" />
                </span>
                <h2
                  id="sheet-form-title"
                  className="mt-4 text-xl font-bold text-slate-950 dark:text-white"
                >
                  Create Google Sheet
                </h2>
                <p className="mt-1 text-sm leading-6 text-slate-500 dark:text-slate-400">
                  We’ll create and connect a new spreadsheet for saved card data.
                </p>
              </div>
              <button
                type="button"
                disabled={connectingSheet}
                onClick={() => {
                  setShowCreateSheetModal(false);
                  setNewSheetName("");
                }}
                aria-label="Close create sheet form"
                className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-slate-100 text-slate-500 dark:bg-slate-800 dark:text-slate-300"
              >
                <Icon name="close" className="h-5 w-5" />
              </button>
            </header>

            <form
              className="mt-6"
              onSubmit={(event) => {
                event.preventDefault();
                redirectToGoogleConnect({
                  createNew: true,
                  sheetName: newSheetName,
                });
              }}
            >
              <label className="block">
                <span className="mb-1.5 block text-xs font-bold text-slate-700 dark:text-slate-300">
                  Spreadsheet name
                </span>
                <input
                  autoFocus
                  type="text"
                  value={newSheetName}
                  onChange={(event) => setNewSheetName(event.target.value)}
                  placeholder="e.g. CPHI Leads 2026"
                  className="min-h-12 w-full rounded-xl border border-slate-200 bg-white px-3.5 text-sm text-slate-900 outline-none transition placeholder:text-slate-400 focus:border-indigo-500 focus:ring-4 focus:ring-indigo-100 dark:border-slate-700 dark:bg-slate-950 dark:text-white dark:focus:ring-indigo-950"
                />
              </label>
              <div className="mt-5 grid grid-cols-2 gap-2">
                <button
                  type="button"
                  disabled={connectingSheet}
                  onClick={() => {
                    setShowCreateSheetModal(false);
                    setNewSheetName("");
                  }}
                  className="min-h-12 rounded-xl border border-slate-200 text-sm font-bold text-slate-600 transition hover:bg-slate-50 disabled:opacity-50 dark:border-slate-700 dark:text-slate-300 dark:hover:bg-slate-800"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={connectingSheet || !newSheetName.trim()}
                  className="inline-flex min-h-12 items-center justify-center gap-2 rounded-xl bg-indigo-600 px-3 text-sm font-bold text-white transition hover:bg-indigo-700 disabled:opacity-50"
                >
                  <Icon name="plus" className="h-4 w-4" />
                  {connectingSheet ? "Redirecting…" : "Create & connect"}
                </button>
              </div>
            </form>
          </section>
        </div>
      )}
    </main>
  );
};

export default Users;
